import catalogue from '@mathbuddy/adaptive-practice-json/lib/json/skillIdName.json';

/**
 * Every skill the engine can actually serve, and what it is called.
 *
 * The engine ships this list with its content - each entry is one skill: its lo_id, the authored
 * title a teacher reads, and the JSON file the questions come from. It is the same list the engine
 * itself looks a skill up in, so a skill missing from here is one the engine has no content for.
 *
 * WHY READ IT DIRECTLY. `Content.getSkillName()` answers with the FILE name ("add3Numbers1to10"),
 * which is a working name rather than one to put in front of a teacher, and the engine exposes no
 * way to reach the title beside it. Reading the list is also far cheaper than the alternative: the
 * only other way to ask whether the engine has a skill is to LOAD it, which pulls in the whole
 * question bundle for every objective in a sub-topic just to decide what to put in a dropdown.
 *
 * Interim, deliberately: the engine's own package should offer this (its HOTS side already does,
 * as `HotsJSON.getBanks()`). When it does, this file becomes one call.
 */
interface CatalogueSkill {
  id: string;
  title?: string;
  file?: string;
}

const byId: { [loId: string]: string } = {};
((catalogue as any)?.topics || []).forEach((topic: any) => {
  (topic?.skills || []).forEach((skill: CatalogueSkill) => {
    const title = String(skill?.title || '').trim();
    if (skill?.id && title) byId[String(skill.id)] = title;
  });
});

/** What this skill is called, or '' when the engine does not have it. */
export const skillTitle = (loId: string): string => byId[String(loId)] || '';

/**
 * The skills of a sub-topic that the engine can serve, in the order the plan lists them.
 *
 * An objective the engine has no content for is left out rather than offered and failing the moment
 * a teacher picks it - a plan carries objectives that adaptive practice has never covered.
 */
export const skillsFromPlan = (
  loIds: string[],
): { loId: string; name: string }[] =>
  loIds
    .map((loId) => ({ loId: String(loId), name: skillTitle(loId) }))
    .filter((skill) => Boolean(skill.name));
